import React, { useState, useMemo } from 'react';
import Button from '../../../../common/components/Button';
import Input from '../../../../common/components/Input';
import Modal from '../../../../common/components/Modal';
interface MoodleConfigFormProps {
  config: {
    lmsUrl: string;
    refreshToken: string;
    proviewWsFunction: string;
    proviewName:string;
  };
  setConfig: React.Dispatch<React.SetStateAction<any>>;
  handleUpdateConfiguration: (e: React.FormEvent<HTMLFormElement>) => void;
}

const MoodleConfigForm: React.FC<MoodleConfigFormProps> = ({ config, setConfig, handleUpdateConfiguration,}) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [urlError, setUrlError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showCourseIdModal, setShowCourseIdModal] = useState(false);
  const [courseId, setCourseId] = useState<string>('');
  const [enrolledUsers, setEnrolledUsers] = useState<{ username: string; email: string }[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const verifyCredentials = async () => {
    // eslint-disable-next-line no-restricted-globals
    event.preventDefault();
    setShowCourseIdModal(false);
    setShowSuccess(false);
    setLoading(true)
    try {
      if (!config.refreshToken || !config.lmsUrl || !config.proviewWsFunction || !courseId) {
        throw new Error();
      }

      const url = `${config.lmsUrl}?wstoken=${config.refreshToken}&moodlewsrestformat=json&wsfunction=${config.proviewWsFunction}&courseid=${courseId}`;

      const response = await fetch(url, {
        method: 'GET',
      });

      const text = await response.text();

      let data;
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error();
      }

      if (!Array.isArray(data)) {
        throw new Error();
      }

      const users = data.map((user: any) => ({
        username: user.username,
        email: user.email,
      }));

      setEnrolledUsers(users);
      setIsAuthenticated(true);
      setShowSuccess(true);
      setUrlError(null);

    } catch {
      setIsAuthenticated(false);
      setEnrolledUsers([]);
      setUrlError('The data entered is incorrect, please check and try again.');
    }
    finally {
      setLoading(false);
    }
  };


  const isValidUrl = (url: string): boolean => {
    try {
      if (url !== url.trim()) return false;
      const urlObj = new URL(url);
      if (/\/+$/.test(url)) return false;
      return /^https?:/.test(urlObj.protocol);
    } catch (e) {
      return false;
    }
  };

  const isFormValid = useMemo(() => {
    return (
      config.proviewName.trim() !== '' &&
      config.refreshToken.trim() !== '' &&
      config.proviewWsFunction.trim() !== '' &&
      isValidUrl(config.lmsUrl)
    );
  }, [config]);
  const handleModal = () => {
    // eslint-disable-next-line no-restricted-globals
    event.preventDefault()
    setShowCourseIdModal(true);
  };
  return (
    <form onSubmit={handleUpdateConfiguration}>
      <div className="flex flex-col space-y-6 mx-auto p-4 bg-white">
        <Input
          type="text"
          label="Name :"
          autoComplete="new-name"
          disabled={isAuthenticated}
          value={config.proviewName}
          className='block mb-2'
          onChange={(event) =>
            setConfig((prev) => ({ ...prev, proviewName: event.target.value }))
          }
          required={true}
        />
        <Input
          type="text"
          label="Web Service Function :"
          autoComplete="new-function"
          disabled={isAuthenticated}
          value={config.proviewWsFunction}
          className='block mb-2'
          onChange={(event) =>
            setConfig((prev) => ({ ...prev, proviewWsFunction: event.target.value }))
          }
          required={true}
        />
        <Input
          type="text"
          label="Web Service Token :"
          autoComplete="new-token"
          disabled={isAuthenticated}
          value={config.refreshToken}
          className='block mb-2'
          onChange={(event) =>
            setConfig((prev) => ({ ...prev, refreshToken: event.target.value }))
          }
          required={true}
        />
        <Input
          type="text"
          label="Web Service URL :"
          value={config.lmsUrl}
          disabled={isAuthenticated}
          onChange={(event) => {
            const value = event.target.value;
            setConfig((prev) => ({ ...prev, lmsUrl: value }));
            if (!isValidUrl(value)) {
              setUrlError("Please enter a valid URL without trailing slashes or extra spaces.");
            } else {
              setUrlError(null);
            }
          }}
          required={true}
        />
        {urlError && (
          <div className="text-sm font-semibold text-red-800 mt-2">
            {urlError}
          </div>
        )}
        {showCourseIdModal && (
          <Modal show={showCourseIdModal} onCancel={() => setShowCourseIdModal(false)}>
            <div className="w-full max-w-md p-4 bg-white overflow-hidden">
              <div className="flex flex-col space-y-4 overflow-y-auto">
                <h3 className="block text-sm font-medium text-gray-700 mb-1">
                  Please enter course ID to verify configuration
                </h3>
                <input
                  type="text"
                  value={courseId}
                  onChange={(e) => setCourseId(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-emerald-500 focus:border-emerald-500"
                  placeholder="Please enter course id"
                />
              </div>
              <div className="space-x-4 p-2">
                <button
                  onClick={() => setShowCourseIdModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={verifyCredentials}
                  disabled={!courseId}
                  className="px-4 py-2 text-sm font-medium text-white bg-emerald-900 rounded-md hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Confirm
                </button>
              </div>
            </div>
          </Modal>
        )}
        <div className="flex gap-4 mt-4">
          <Button
            type="button"
            label="Verify"
         onClick={handleModal}
            className="mt-4 bg-emerald-900 hover:bg-emerald-700 text-gray-200 font-bold py-2 px-4 rounded-full inline-flex items-center"
          />
          <Button
            type="submit"
            disabled={!isAuthenticated || !isFormValid}
            className={`mt-4 ${!isAuthenticated || !isFormValid ? 'bg-gray-400 cursor-not-allowed' : 'bg-emerald-900 hover:bg-emerald-700'} text-gray-200 font-bold py-2 px-4 rounded-full inline-flex items-center`}
            label="Update Configuration"
          />
        </div>
        {loading && (
          <div className="text-sm font-semibold text-black-700 mt-2">Fetching details...</div>
        )}
        {!loading && showSuccess && (
          <div className="text-sm font-semibold text-green-700 mt-2">
            Successfully verified! Next, click on Update Configuration.
            {enrolledUsers.length > 0 && (
              <div className="overflow-x-auto mt-4">
                <table className="min-w-full text-sm border border-gray-200 rounded-md">
                  <thead className="bg-emerald-100">
                  <tr>
                    <th className="text-left px-4 py-2 border-b border-gray-300">No:</th>
                    <th className="text-left px-4 py-2 border-b border-gray-300">Username</th>
                    <th className="text-left px-4 py-2 border-b border-gray-300">Email</th>
                  </tr>
                  </thead>
                  <tbody className="bg-white">
                  {enrolledUsers.map((user, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-2 border-b border-gray-200">{index + 1}</td>
                      <td className="px-4 py-2 border-b border-gray-200">{user.username}</td>
                      <td className="px-4 py-2 border-b border-gray-200">{user.email}</td>
                    </tr>
                  ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </form>
  );
};

export default MoodleConfigForm;
